import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import { store } from 'components/Store';
import { useEmailState } from 'components/EmailStateProvider';
import useEmailComponentFunctions from 'components/Dashboard/Influence/CrmEmails/useEmailComponentFunctions';
import useGetInitialEmailAddress from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/useGetInitialEmailAddress';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import normalizeCrmEmailModel from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/normalizeCrmEmailModel';
import { resolveEmailRouteState } from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/emailRouteState';
import CrmEmailComponentSkeleton from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/CrmEmailComponentSkeleton';
import EmailPerformanceTabs from 'components/Dashboard/Influence/EmailPerformance/components/EmailPerformanceTabs';
import EmailComponentBody from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/EmailComponentBody';
import SentPerformance from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/SentPerformance/SentPerformance';
import EmailRecipients from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/EmailRecipients/EmailRecipients';
import hasAnchorTag from 'components/Dashboard/utilities/hasAnchorTag';

function EmailPerformanceReadOnly() {
  const globalState = useContext(store);
  const { state } = globalState;
  const { listDomainsVerified } = state;

  const emailAddresses = listDomainsVerified?.verifiedEmailAddresses ?? [];

  const { emailState, setEmailState } = useEmailState();
  const { currentEmailDetails } = emailState;

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { crmEmailId } = params;
  const routeState = resolveEmailRouteState({ location, defaultTab: 'Performance' });

  const { getEmail } = useEmailComponentFunctions();
  const { getInitialSenderEmailAddress } = useGetInitialEmailAddress();
  const { addMetaTags } = useAddMetaTags();

  const [isLoading, setIsLoading] = useState(true);
  const [isEditorOnLoad, setIsEditorOnLoad] = useState(true);
  const [searchValueRecipients, setSearchValueRecipients] = useState('');
  const [activeTabEmailSent, setActiveTabEmailSentState] = useState(routeState.requestedTab);
  const [activeRecipientsOption, setActiveRecipientsOption] = useState(routeState.requestedRecipientsOption);
  const autofocusFieldFromUrl = useRef(null);

  const setActiveTabEmailSent = (value) => {
    setActiveTabEmailSentState(value);
    setEmailState('activeTabEmailSent', value);
  };

  const requestedEmailId = routeState.cloneEmailId ?? crmEmailId;
  const isLoadedRequestedEmail =
    currentEmailDetails?.crmEmailId != null && String(currentEmailDetails.crmEmailId) === String(requestedEmailId);
  const isEmailTabLoading = isLoading || !isLoadedRequestedEmail;
  const isRecipientsTabLoading = isLoading || !isLoadedRequestedEmail;

  useLayoutEffect(() => {
    setActiveTabEmailSentState(routeState.requestedTab);
    setEmailState('activeTabEmailSent', routeState.requestedTab);
    setActiveRecipientsOption(routeState.requestedRecipientsOption);
  }, [crmEmailId, routeState.requestedTab, routeState.requestedRecipientsOption]);

  useEffect(() => {
    autofocusFieldFromUrl.current = routeState.autofocusFieldFromUrl;
  }, [routeState.autofocusFieldFromUrl]);

  useEffect(() => {
    if (activeTabEmailSent === 'Email') {
      setIsEditorOnLoad(true);
    }
  }, [activeTabEmailSent, requestedEmailId]);

  useEffect(() => {
    const source = new AbortController();

    const run = async () => {
      setIsLoading(true);
      try {
        if (listDomainsVerified?.domainsProccessing) {
          return;
        }

        let crmEmailModel = await getEmail({ emailId: requestedEmailId, source });
        if (!crmEmailModel) return;

        if (crmEmailModel.status === 403 || crmEmailModel.status === 404) {
          navigate('/people/emails', { replace: true });
          return;
        }

        crmEmailModel = normalizeCrmEmailModel({
          crmEmailModel,
          getInitialSenderEmailAddress,
          emailAddresses,
        });

        addMetaTags({
          title: `${crmEmailModel?.subject?.length ? `${crmEmailModel?.subject} - ` : ''}${
            crmEmailModel?.status ?? 'Email'
          } - PolicyMogul`,
          hash: null,
        });

        setEmailState('currentEmailDetails', crmEmailModel);
      } catch (e) {
        // Intentionally quiet: existing email page behavior swallows fetch errors.
      } finally {
        setIsLoading(false);
      }
    };

    run();
    return () => {
      source.abort('Email performance email cancelled by the user');
    };
  }, [crmEmailId, requestedEmailId, listDomainsVerified?.domainsProccessing, emailAddresses?.length]);

  const refreshEmailData = async (source, updateMailMergeData = false) => {
    try {
      const result = await getEmail({ emailId: requestedEmailId, source });
      if (!result) return;

      let crmEmailModel = result;
      if (updateMailMergeData) {
        crmEmailModel = {
          ...currentEmailDetails,
          mailMergeFields: result?.mailMergeFields,
          hasMailMergeData: result?.hasMailMergeData,
        };
      }

      crmEmailModel = normalizeCrmEmailModel({
        crmEmailModel,
        getInitialSenderEmailAddress,
        emailAddresses,
      });

      setEmailState('currentEmailDetails', crmEmailModel);
    } catch {
      // keep silent
    }
  };

  const initialToValue = () => {
    const prepopulatedContacts = routeState.prepopulatedContacts;
    if (prepopulatedContacts) {
      return [...prepopulatedContacts].sort((a, b) => {
        if (a.label[0] > b.label[0]) return 1;
        if (a.label[0] < b.label[0]) return -1;
        return 0;
      });
    }

    if (currentEmailDetails?.sendRecords?.length > 0) {
      return currentEmailDetails.sendRecords.map((record) => ({
        value: record.crmContactId,
        imageId: record.imageId,
        label: record.fullName,
        id: record.crmContactId,
        email: record.emailAddress,
      }));
    }

    return [];
  };

  const hasLink = useMemo(() => hasAnchorTag(currentEmailDetails?.body), [currentEmailDetails?.body]);

  return (
    <div className='h-100 email-component-wrapper'>
      <div className='d-flex flex-column h-100'>
        <div className='row mx-0 h-100'>
          <div
            className='flex-grow-1 h-100 position-relative'
            style={{ minWidth: 0, ...(isLoading ? { overflow: 'hidden' } : null) }}
          >
            <EmailPerformanceTabs
              activeTab={activeTabEmailSent}
              setActiveTab={setActiveTabEmailSent}
              searchValue={searchValueRecipients}
              setSearchValue={setSearchValueRecipients}
              emailId={requestedEmailId}
              emailSubject={currentEmailDetails?.subject}
            />

            {activeTabEmailSent === 'Email' && (
              <>
                {isEmailTabLoading ? (
                  <CrmEmailComponentSkeleton />
                ) : (
                  <EmailComponentBody
                    currentEmailDetails={currentEmailDetails}
                    disabledCondition
                    disabledToCondition
                    handleSubject={() => {}}
                    handleBody={() => {}}
                    initialToValue={initialToValue}
                    emailAddresses={emailAddresses}
                    handleFrom={() => {}}
                    autofocusFieldFromUrl={autofocusFieldFromUrl}
                    setEditorOnLoad={setIsEditorOnLoad}
                    setActiveTab={setActiveTabEmailSent}
                    setActiveRecipientsOption={setActiveRecipientsOption}
                    skeletonCondition={isEditorOnLoad}
                    isLoadingWritingAssistant={false}
                    showSendPreviewPopUp={false}
                    refreshEmailData={refreshEmailData}
                  />
                )}
              </>
            )}

            {activeTabEmailSent === 'Performance' && (
              <SentPerformance
                emailId={true}
                setActiveTab={setActiveTabEmailSent}
                setActiveRecipientsOption={setActiveRecipientsOption}
                hasLink={hasLink}
                currentEmailDetails={currentEmailDetails}
              />
            )}

            {activeTabEmailSent === 'Recipients' && (
              <EmailRecipients
                sendRecords={currentEmailDetails?.sendRecords}
                emailId={true}
                searchValue={searchValueRecipients}
                setSearchValue={setSearchValueRecipients}
                activeOption={activeRecipientsOption}
                setActiveOption={setActiveRecipientsOption}
                isLoading={isRecipientsTabLoading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailPerformanceReadOnly;
